import { Mic, MicOff, Loader2 } from "lucide-react";

// Single-shot mic button rendered inside the chat composer footer.
// Behaviour is fully controlled by the parent — this component only
// renders the correct visual state and forwards click intent.
//
// States (visual only, no vendor names, no engine references):
//   idle          → primary mic, tappable
//   listening     → pulsing red, tap-to-stop
//   denied        → disabled with tooltip "Mikrofon izni verilmedi"
//   unsupported   → disabled with tooltip "Bu tarayıcı sesli girişi desteklemiyor"
//   error         → disabled with tooltip (Turkish error string from hook)
export function MicButton({
  status = "idle",
  supported = true,
  errorMessage = null,
  onClick,
  disabled = false,
}) {
  const isListening = status === "listening";
  const isUploading = status === "uploading";
  const isDenied = status === "denied";
  const isUnsupported = !supported || status === "unsupported";
  const isError = status === "error";
  const isDisabled = disabled || isDenied || isUnsupported || isUploading;

  let title = "Sesli giriş";
  if (isUnsupported) title = "Bu tarayıcı sesli girişi desteklemiyor";
  else if (isDenied) title = "Mikrofon izni verilmedi";
  else if (isError && errorMessage) title = errorMessage;
  else if (isListening) title = "Dinlemeyi durdur";
  else if (isUploading) title = "Yazıya çevriliyor";

  const base =
    "inline-flex items-center justify-center h-9 w-9 rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1";
  const tone = isListening
    ? "bg-red-50 border-red-200 text-red-700 hover:bg-red-100 focus-visible:ring-red-300 animate-pulse"
    : isUploading
      ? "bg-amber-50 border-amber-200 text-amber-700 cursor-progress"
      : isDisabled
        ? "bg-white border-zinc-200 text-zinc-300 cursor-not-allowed"
        : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-300 hover:text-[#1E3A8A] focus-visible:ring-[#1E3A8A]/30";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      className={`${base} ${tone}`}
      title={title}
      aria-label={title}
      aria-pressed={isListening}
      aria-busy={isUploading}
      data-testid="voice-mic-button"
      data-voice-status={status}
    >
      {isListening || isUploading ? (
        <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
      ) : isUnsupported || isDenied ? (
        <MicOff className="w-4 h-4" aria-hidden />
      ) : (
        <Mic className="w-4 h-4" aria-hidden />
      )}
      <span className="sr-only">{title}</span>
    </button>
  );
}
